import React, { useEffect, useMemo } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { Provider as ReduxProvider } from 'react-redux'
import { store } from '../store'
import Area from './Area'
import { useToolSyncPubsub, useToolSyncRepo } from '../helpers/tool-sync'
import { YasguiEditor } from './YasguiEditor'
import { getRepoName } from '../api/app-login'
import { SparqlTriggerExecutor } from './SparqlTriggerExecutor'
import { GLOBAL_NAMESPACE, ComponentNamespaceContext, publishDict, getPubsub } from '../redux/pubsub'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { COMPONENT_APP_SCHEMA, type ValidComponentApp } from './widgets/ComponentWidget/ImportAppJSON'
import { getAuthHeaders } from '../api/auth'
import {
  StandalonePageBody,
  StandalonePageMessage,
  StandalonePageShell,
  StandalonePageTopBar,
} from './ui-primitives/panel-chrome'
import { useVoteToDisableUserActions } from '../redux/current-viewer'
import { AppCtxProvider } from '../hooks/use-app-ctx'
import { BackendEventsVarPublisher } from "../api/backend-events"

export type ToolName = 'rule-editor' | 'ontology-editor' | 'ontology-viewer'

const TOOL_LABELS: Record<ToolName, string> = {
  'rule-editor': 'Rule Editor',
  'ontology-editor': 'Ontology Editor',
  'ontology-viewer': 'Ontology Viewer',
}

const TOOL_API_URLS: Record<ToolName, string> = {
  'rule-editor': 'https://app.wistor.nl/api/projects/WistorBaseApps/viewers/Hp3Ib',
  'ontology-editor': 'https://app.wistor.nl/api/projects/WistorBaseApps/viewers/rA2jv',
  'ontology-viewer': 'https://app.wistor.nl/api/projects/WistorBaseApps/viewers/XaE6W',
}

function useQueryParams() {
  return new URLSearchParams(useLocation().search)
}

function useToolConfig(toolName: ToolName) {
  const apiURL = TOOL_API_URLS[toolName]
  return useQuery<ValidComponentApp>({
    queryKey: ['tool-config', toolName, apiURL],
    queryFn: async () => {
      const res = await axios.get(apiURL, { headers: getAuthHeaders() })
      return COMPONENT_APP_SCHEMA.parse(res.data)
    },
    retry: 1,
    staleTime: Infinity,
  })
}

/**
 * Standalone page rendered at /tools/sparql?repo=...&repoName=...
 */
export function StandaloneSparqlPage() {
  const params = useQueryParams()
  const initialRepo = params.get('repo') || ''
  const initialLocalRepo = params.get('localRepo') || undefined
  const initialRepoName = params.get('repoName') || ''
  const initialQuery = params.get('query') || undefined
  const { repoURL, localRepoURL } = useToolSyncRepo(initialRepo, initialLocalRepo)
  const bbsparqlendpoint = `repo=${initialRepoName}`

  const repoName = React.useMemo(
    () => initialRepoName || getRepoName(''),
    [initialRepoName, repoURL, localRepoURL]
  )

  useEffect(() => {
    document.title = `SPARQL — ${repoName || 'Editor'}`
  }, [repoName])

  useToolSyncPubsub()

  return (
    <ReduxProvider store={store}>
      <AppCtxProvider repoURL={repoURL} localRepoURL={localRepoURL} bbsparqlendpoint={bbsparqlendpoint}>
        <ComponentNamespaceContext.Provider value={GLOBAL_NAMESPACE}>
          <StandalonePageShell>
            <StandalonePageTopBar title="SPARQL" detail={<span title={repoURL}>SPARQL endpoint: {repoURL}</span>} />
            <StandalonePageBody>
              <YasguiEditor
                id="standalone-sparql-yasgui"
                repoURL={repoURL}
                repoName={repoName ?? ''}
                localRepoURL={localRepoURL}
                initialQuery={initialQuery}
              />
            </StandalonePageBody>
          </StandalonePageShell>
        </ComponentNamespaceContext.Provider>
      </AppCtxProvider>
    </ReduxProvider>
  )
}

/**
 * Standalone page for tool apps (Rule Editor, Ontology Editor, Ontology Viewer).
 * Rendered at /tools/:toolName?repo=...
 *
 * Fetches the app config from the platform API, validates it, and renders
 * an Area component in preview mode. Syncs repoURL via BroadcastChannel.
 */
export function StandaloneToolPage() {
  const { toolName } = useParams<{ toolName: string }>()
  const params = useQueryParams()
  const initialRepo = params.get('repo') || ''
  const initialLocalRepo = params.get('localRepo') || undefined
  const { repoURL, localRepoURL } = useToolSyncRepo(initialRepo, initialLocalRepo)
  const bbsparqlendpoint = useMemo(() => `repo=${initialRepo.split('/').at(-1)}`, [initialRepo])
  const voteToDisableUserActions = useVoteToDisableUserActions()

  const tool = toolName as ToolName
  const title = TOOL_LABELS[tool] ?? toolName

  const repoName = React.useMemo(
    () => getRepoName(''), // DEBT: using empty string is sus
    [repoURL, localRepoURL]
  )

  const { data: config, isLoading, error } = useToolConfig(tool)

  useEffect(() => {
    document.title = title
  }, [title])

  // Apply default parameter values
  React.useEffect(() => {
    if (!config?.parameterValues) return
    try {
      const defaults = JSON.parse(config.parameterValues)
      publishDict(defaults, GLOBAL_NAMESPACE, 'standalone-tool-page:parameterValues')
    } catch (e) {
      console.error('Failed to parse parameterValues', e)
    }
  }, [config?.parameterValues])

  const pubsub = React.useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => getPubsub(GLOBAL_NAMESPACE)
  )

  // Avoid rendering when matched by /tools/sparql
  if (toolName === 'sparql') return null

  if (!(tool in TOOL_API_URLS)) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-900 text-white">
        <p>Unknown tool: {toolName}</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <StandalonePageShell>
        <StandalonePageTopBar title={title} />
        <StandalonePageMessage>Loading {title}…</StandalonePageMessage>
      </StandalonePageShell>
    )
  }

  if (error || !config) {
    return (
      <StandalonePageShell>
        <StandalonePageTopBar title={title} />
        <StandalonePageMessage variant="error">
          Failed to load {title}: {error instanceof Error ? error.message : 'Unknown error'}
        </StandalonePageMessage>
      </StandalonePageShell>
    )
  }

  return (
    <AppCtxProvider
      displayChromiumTranslateErrors={false}
      bbsparqlendpoint={bbsparqlendpoint}
      repoURL={repoURL}
      localRepoURL={localRepoURL}
    >
    <ReduxProvider store={store}>
      <ComponentNamespaceContext.Provider value={GLOBAL_NAMESPACE}>
        <StandalonePageShell>
          <StandalonePageTopBar title={title} detail={<span title={repoURL}>{repoURL}</span>} />
          <StandalonePageBody>

          <BackendEventsVarPublisher />
          {config.triggers?.map((trigger) => (
            <SparqlTriggerExecutor
              key={trigger.label}
              templateId={trigger.label}
              sparqlTemplate={trigger.sparqlQuery}
              signalIsFetching={(isFetching) => {
                if (trigger.voteForUserActionsDisabledWhenLoading)
                  voteToDisableUserActions(trigger.label, isFetching)
              }}
            />
          ))}

            <div style={{ position: 'relative', flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <Area
              notEditable
              config={config.mainArea}
              onChange={() => { throw new Error('StandaloneToolPage: Editing this app is not supported') }}
              mode="preview"
              pubsub={pubsub}
              repoName={repoName}
              repoURL={repoURL}
              localRepoURL={localRepoURL}
              save={() => Promise.reject(new Error('StandaloneToolPage: Saving this app is not supported'))}
              namespace={GLOBAL_NAMESPACE}
            />
          </div>
          </StandalonePageBody>
        </StandalonePageShell>
      </ComponentNamespaceContext.Provider>
    </ReduxProvider>
    </AppCtxProvider>
  )
}
